import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import axios from "axios";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import apiEndpoints from "../../../apiconfig";
import { apiErrorMessage } from "./apiErrorMessage";

const GST_RATES = [0, 5, 12, 18, 28];
const GSTIN_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

const profileGstin = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return (user?.gstIn || "").toUpperCase();
  } catch (e) {
    return "";
  }
};

const round2 = (n) => Math.round(n * 100) / 100;

// Same maths as computeGst() in gst_helper.php
const splitGst = (amount, rate, mode) => {
  if (mode === "exclusive") {
    const gst = round2((amount * rate) / 100);
    return { taxable: amount, gst, total: round2(amount + gst) };
  }
  const taxable = round2((amount * 100) / (100 + rate));
  return { taxable, gst: round2(amount - taxable), total: amount };
};

export default function GstSettings({ glass }) {
  const token = localStorage.getItem("token");
  const { showSnackbar } = useSnackbar();

  const [form, setForm] = useState({
    enabled: false,
    gstin: "",
    rate: 0, // education services are GST-exempt
    type: "CGST_SGST",
    mode: "inclusive",
    sac_code: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null); // what the server has stored

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${apiEndpoints.settings}?action=get_gst`, authHeaders);
        if (typeof res.data === "string") throw new Error("NOT_DEPLOYED");
        if (res.data?.status !== "success") throw new Error(res.data?.message);
        const d = res.data.data || {};
        setSaved(d);
        setForm({
          enabled: !!d.enabled,
          gstin: d.gstin || profileGstin(),
          rate: d.rate !== undefined && d.rate !== null ? Number(d.rate) : 0,
          type: d.type || "CGST_SGST",
          mode: d.mode === "exclusive" ? "exclusive" : "inclusive",
          sac_code: d.sac_code || "",
        });
      } catch (err) {
        showSnackbar(apiErrorMessage(err, "settings.php", "Failed to load GST settings"), "error");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = () => {
    const e = {};
    const rate = Number(form.rate);
    if (form.gstin && !GSTIN_REGEX.test(form.gstin)) e.gstin = "Enter a valid 15-character GSTIN";
    if (form.enabled && !form.gstin && rate > 0) e.gstin = "GSTIN is required to charge GST above 0%";
    if (Number.isNaN(rate) || rate < 0 || rate > 28) e.rate = "Rate must be between 0 and 28";
    if (form.sac_code && !/^\d{4,8}$/.test(form.sac_code)) e.sac_code = "4–8 digits";
    setErrors(e);
    const first = Object.values(e)[0];
    if (first) showSnackbar(`Not saved: ${first}`, "error");
    return !first;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${apiEndpoints.settings}?action=save_gst`,
        { ...form, rate: Number(form.rate) },
        authHeaders,
      );
      if (typeof res.data === "string") throw new Error("NOT_DEPLOYED");
      if (res.data?.status !== "success") throw new Error(res.data?.message);
      setSaved(res.data.data || { ...form });
      showSnackbar(res.data.message, "success");
    } catch (err) {
      showSnackbar(apiErrorMessage(err, "settings.php", "Failed to save GST settings"), "error");
    } finally {
      setSaving(false);
    }
  };

  const savedEnabled = !!saved?.enabled;
  const dirty =
    !!saved &&
    (form.enabled !== savedEnabled ||
      form.gstin !== (saved.gstin || "") ||
      Number(form.rate) !== Number(saved.rate) ||
      form.type !== saved.type ||
      form.mode !== (saved.mode || "inclusive") ||
      form.sac_code !== (saved.sac_code || ""));

  const rate = Number(form.rate) || 0;
  const example = splitGst(10000, rate, form.mode);
  const taxLine =
    form.type === "IGST"
      ? `₹${example.gst.toFixed(2)} IGST`
      : `₹${round2(example.gst / 2).toFixed(2)} CGST + ₹${round2(example.gst - round2(example.gst / 2)).toFixed(2)} SGST`;

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <ReceiptLongIcon sx={{ color: "#3F51B5" }} />
        <Typography variant="h6" fontWeight={700}>
          GST on Fee Receipts
        </Typography>
      </Stack>

      <Paper sx={{ ...glass, p: { xs: 2, sm: 3 } }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <FormControlLabel
              control={
                <Switch checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} />
              }
              label={
                savedEnabled
                  ? `GST is ON — saved (${saved.gstin}, ${saved.rate}%)`
                  : "GST is OFF"
              }
              sx={{ mb: 2 }}
            />

            {dirty && (
              <Alert
                severity="warning"
                sx={{ mb: 2, borderRadius: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={save} disabled={saving}>
                    {saving ? "Saving..." : "Save now"}
                  </Button>
                }
              >
                You have unsaved changes. GST only applies after you save.
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="GSTIN"
                  fullWidth
                  size="small"
                  value={form.gstin}
                  onChange={(e) => set("gstin", e.target.value.toUpperCase().trim())}
                  inputProps={{ maxLength: 15 }}
                  error={!!errors.gstin}
                  helperText={errors.gstin || "Pre-filled from your admin profile if set"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="GST Rate"
                  fullWidth
                  size="small"
                  value={GST_RATES.includes(rate) ? rate : ""}
                  onChange={(e) => set("rate", Number(e.target.value))}
                  error={!!errors.rate}
                  helperText={errors.rate}
                >
                  {GST_RATES.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r === 0 ? "0% (Exempt — education)" : `${r}%`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Tax Type"
                  fullWidth
                  size="small"
                  value={form.type}
                  onChange={(e) => set("type", e.target.value)}
                  helperText={
                    form.type === "IGST"
                      ? "Parent is in a different state"
                      : "Parent is in the same state as the school"
                  }
                >
                  <MenuItem value="CGST_SGST">CGST + SGST (intra-state)</MenuItem>
                  <MenuItem value="IGST">IGST (inter-state)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="SAC Code (optional)"
                  fullWidth
                  size="small"
                  value={form.sac_code}
                  onChange={(e) => set("sac_code", e.target.value.replace(/\D/g, ""))}
                  inputProps={{ maxLength: 8 }}
                  error={!!errors.sac_code}
                  helperText={errors.sac_code || "Service code printed next to the fee"}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  GST on invoice
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  color="primary"
                  size="small"
                  value={form.mode}
                  onChange={(e, v) => v && set("mode", v)}
                  sx={{ flexWrap: "wrap" }}
                >
                  <ToggleButton value="inclusive" sx={{ px: 2, textTransform: "none" }}>
                    Inclusive — GST included in amount paid
                  </ToggleButton>
                  <ToggleButton value="exclusive" sx={{ px: 2, textTransform: "none" }}>
                    Exclusive — GST added on top
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                  {form.mode === "inclusive"
                    ? `₹10,000 paid → ₹${example.taxable.toFixed(2)} fee value + ${taxLine} = ₹${example.total.toFixed(2)}`
                    : `₹10,000 towards the fee + ${taxLine} = ₹${example.total.toFixed(2)} collected`}
                  {rate === 0 && " (no difference at 0%)"}
                </Typography>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
              School fees are educational services and are <b>exempt from GST</b>, so every
              receipt shows GST at <b>0%</b> by default — CGST ₹0.00 + SGST ₹0.00 with the note
              "Educational services — exempt from GST (0%)". Fee balances never change: in
              exclusive mode the GST is collected on top of the fee amount.
              {rate > 0 && " The rate and mode apply to payments recorded after saving; earlier receipts stay as issued."}
            </Alert>

            {rate > 0 && (
              <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
                Education services are GST-exempt in India. Only use a rate above 0% if your
                accountant confirms a specific service is taxable.
              </Alert>
            )}

            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button variant="contained" onClick={save} disabled={saving} sx={{ borderRadius: 999, px: 4 }}>
                {saving ? "Saving..." : "Save GST Settings"}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
